
import Vue from 'vue'
import SubContentType from '@/Models/Static/OrderContentType'
import MenuChoice from '@/Models/Static/MenuChoice'

const state = () => ({
  /**
   * Contains the item that the user is adding to the order or editing.
   * @type Object
   */
  item: {},

  /**
   * Determine if the user is adding a menu to the order or editing it.
   * @type Boolean
   */
  editing: false,

  /**
   * Contains all the Menu Menu Categories with their items.
   * @type Array
   */
  categories: [],

  /**
   * These are the v-window, v-xxx-group components actual values.
   * @type Number
   */
  category: 0,
  dishOrder: 0,

  /**
   * Holds the number of menus that we are about to complete in fast entry mode.
   * @type Number
   */
  quantity: 0,

  /**
   * Contains all the SubContents.
   * @type Array
   */
  contents: [],

  /**
   * Configure the hint's warning.
   * @type Object
   */
  warn: {
    showIcon: false,
    showText: false,
    counter: 0,
    ID: undefined,
  },
})

const getters = {
  isNotLastCategory: state => index => index < state.categories.length - 1,
  countItemsInCategory: state => index => state.categories
    .flatMap(({ items }) => items)
    .map(o => o.counter[index])
    .sum()
  ,
}

const mutations = {
  _item (state, v) {
    state.item = v
  },
  _editing (state, v) {
    state.editing = Boolean(v)
  },
  _categories (state, v) {
    state.categories = v
  },
  category (state, v) {
    state.category = v
  },
  nextCategory (state) {
    state.category++
  },
  dishOrder (state, v) {
    state.dishOrder = v
  },
  quantity (state, v) {
    state.quantity = v
  },

  _addRawContent (state, attributes) {
    state.contents.push({
      type: SubContentType.MENU,
      ...attributes,
    })
  },
  addContent (state, { dishOrder, item, modifiers = [] }) {
    state.contents.push({
      type: SubContentType.MENU,
      dishOrder,
      item_id: item.id,
      itemName: item.name,
      menuPivotId: item.menuPivot.id,
      itemPrice: item.menuPivot.price,
      priceWithExtrasIndividual: item.menuPivot.price + modifiers.sum('price'),
      modifiers,
    })
  },
  removeContent (state, index) {
    state.contents.splice(index, 1)
  },
  _removeContents (state) {
    state.contents = []
  },

  itemIncrement (state, { category_index, item_index, dishOrder }) {
    const counter = state.categories.find(o => o.id == dishOrder)?.items[item_index]?.counter
    if (counter) {
      Vue.set(counter, category_index, counter[category_index] + 1)
    }
  },
  itemDecrement (state, { category_index, item_index, dishOrder }) {
    const counter = state.categories.find(o => o.id == dishOrder)?.items[item_index]?.counter
    if (counter) {
      Vue.set(counter, category_index, counter[category_index] - 1)
    }
  },

  failed (state, index) {
    state.categories[index].failed = true
  },
  unfailed (state, index) {
    state.categories[index].failed = false
  },

  warnIcon (state, v) {
    state.warn.showIcon = v
  },
  warnText (state, v) {
    state.warn.showText = v
  },
  warnCounter (state, v) {
    state.warn.counter = v
  },
  warnID (state, v) {
    state.warn.ID = v
  },
}

const actions = {
  init ({ state, commit, dispatch, rootGetters }, payload) {
    commit('category', 0)
    commit('dishOrder', 0)
    commit('_item', payload.item)
    commit('quantity', payload.quantity)
    commit('_editing', payload.editing)
    commit('_removeContents')
    const categories = rootGetters['catalog/menuCategories'](payload.item)
    const counter = new Array(categories.length).fill(0)
    commit('_categories', categories
      .map((category, i) => ({
        ...category,
        index: i,
        failed: false,
        items: rootGetters['catalog/menuItems'](category.id)
          .map((item, j) => ({
            ...item,
            index: j,
            counter: [...counter],
          }))
        ,
      }))
    )
    if (payload.editing) {
      dispatch('_addPayloadContents', payload.menu)
    } else {
      dispatch('_addDefaultContents')
    }
  },
  _addDefaultContents ({ state, commit }) {
    state.categories
      .filter(category => category.isMultipleChoice == MenuChoice.SELECT_DEFAULT)
      .forEach(category => category.items.forEach(item => {
        commit('addContent', {
          item,
          dishOrder: category.id,
        })
        commit('itemIncrement', {
          category_index: category.index,
          item_index: item.index,
          dishOrder: category.id,
        })
      }))
  },
  _addPayloadContents ({ state, commit }, contents) {
    contents.forEach(content => {
      commit('_addRawContent', content)
      // The most common case (the item is inside its category)
      const category = state.categories.find(o => o.id == content.dishOrder)
      let item = category?.items.find(o => o.id == content.item_id)
      if (item) {
        commit('itemIncrement', {
          category_index: category.index,
          item_index: item.index,
          dishOrder: category.id,
        })
        return
      }
      // When a content's dishOrder is changed, we have to search all the items
      // and a known bug appears if there is the same product in two different
      // categories.
      for (const dishOrder of state.categories) {
        item = dishOrder.items.find(o => o.id == content.item_id)
        if (item) {
          commit('itemIncrement', {
            category_index: category.index,
            item_index: item.index,
            dishOrder: dishOrder.id,
          })
          return
        }
      }
      console.warn('Item not found')
    })
  },
  validateCategory ({ state, getters, commit }, index) {
    const count = getters['countItemsInCategory'](index)
    const min = MenuChoice.min(state.categories[index]) * state.quantity
    const max = MenuChoice.max(state.categories[index]) * state.quantity
    commit(count < min || count > max ? 'failed' : 'unfailed', index)
  },
  async validate ({ state, commit, dispatch }) {
    let step = -1
    for (let category of state.categories) {
      await dispatch('validateCategory', category.index)
      if (category.failed && step == -1) {
        step = category.index
      }
    }
    if (step != -1) {
      commit('category', step)
      commit('dishOrder', step)
      return false
    }
    return true
  },
  startWarning ({ state, commit }) {
    commit('warnCounter', 0)
    commit('warnIcon', true)
    commit('warnText', true)
    if (state.warn.ID) {
      clearInterval(state.warn.ID)
    }
    commit('warnID' , setInterval(() => {
      commit('warnIcon' , ! state.warn.showIcon)
      if (state.warn.showIcon) {
        commit('warnCounter', state.warn.counter + 1)
      }
    }, 350))
  },
  stopWarning ({ state, commit }) {
    clearInterval(state.warn.ID)
    commit('warnIcon', false)
    commit('warnText', false)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
